import axios, { CancelToken } from 'axios';
import i18n from 'i18next';

import { getCache } from 'core/cache';

import get from 'lodash/get';
import throttle from 'lodash/throttle';

import { gcsUploadOptions, s3UploadOptions } from './getSocialFileUploadOptions';

const settings = getCache('settings');
const isGCS = settings?.data?.cloud_storage_provider === 'gcs';
const provider = isGCS ? 'gcs' : 's3';

const getUploadOptions = (file, signedRequestObj) => {
  if (provider === 's3') {
    return s3UploadOptions(file, signedRequestObj);
  }
  return gcsUploadOptions(file, signedRequestObj);
};

const getErrorMessage = (error, defaultError, handler) => {
  // don't show error on cancel
  if (error.__CANCEL__) return;
  return handler(get(error, 'response.data.errors.message', '') || error.message || defaultError);
};

const uploadFile = async (documentsUrlPrefix, document, handlers, cancelToken) => {
  try {
    const { data } = await axios.post(`${documentsUrlPrefix}/documents.json`, document, { cancelToken: cancelToken.token });
    handlers.complete(data);
  } catch (error) {
    getErrorMessage(error, i18n.t('error.upload-processing-error'), handlers.error);
  }
};

const onUploadProgress = throttle((progressObject, handlers) => {
  const percentageComplete = Math.floor((progressObject.loaded / progressObject.total) * 100);
  handlers.progress(percentageComplete);
}, 1000);

export default async (file, handlers, { documentsUrlPrefix = '', policyUrlPrefix = '' }) => {
  const url = `${policyUrlPrefix}/${provider}/policy?file_name=${encodeURI(file.name)}&file_type=${file.type}`;
  const cancelToken = CancelToken.source();

  try {
    const { data } = await axios.get(url);
    const uploadOptions = getUploadOptions(file, data);
    const config = {
      headers: {},
      onUploadProgress: (progressObject) => onUploadProgress(progressObject, handlers),
      cancelToken: cancelToken.token,
    };

    handlers.start(cancelToken);

    try {
      await axios.post(uploadOptions.url, uploadOptions.formData, config);

      const document = {
        url: (uploadOptions.provider === 's3' ? encodeURI(uploadOptions.full_url) : uploadOptions.full_url),
        filename: uploadOptions.filename,
        type: uploadOptions.file.type,
      };

      uploadFile(documentsUrlPrefix, document, handlers, cancelToken);
    } catch (error) {
      getErrorMessage(error, i18n.t('error.upload-processing-error'), handlers.error);
    }
  } catch (error) {
    getErrorMessage(error, i18n.t('error.upload-signing-error'), handlers.error);
  }
};
